import React, { Fragment } from "react";
import getDecryptedToken from "../../../../../../api/getDecryptedToken";
import { insertBreaks } from "../../../../../../utils/functions/resize";
import { useNavigate } from "react-router-dom";

const user: string | undefined = getDecryptedToken("userName");
const dateNow = new Date();

function PasTSummary({ item, isMobile }: any) {
  const nav = item?._id;
  const navigate = useNavigate();

  const formatDate = (date: any) => {
    if (!date) return new Date(dateNow).toISOString().split("T")[0];
    return new Date(date).toISOString().split("T")[0];
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Complete":
        return "bg-emerald-100 text-emerald-700";
      case "ongoing":
        return "bg-amber-100 text-amber-700";
      default:
        return "bg-slate-100 text-slate-600";
    }
  };

  // Mobile Card View
  if (isMobile) {
    return (
      <>
        {item?.treatments?.map((treatment: any, index: number) => (
          <div
            key={index}
            onClick={() => navigate(`/client/treatment-diagnose/add-treatment/diagnose/${nav}`)}
            className="bg-white rounded-lg shadow-sm border border-slate-100 p-3 cursor-pointer hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between mb-2">
              <div>
                <p className="text-xs font-semibold text-slate-800">
                  {treatment?.treatment}
                  {treatment?.type ? ` - ${treatment?.type}` : ""}
                  {treatment?.subtype ? ` - ${treatment?.subtype}` : ""}
                </p>
                <p className="text-[10px] text-slate-500 mt-0.5">{formatDate(treatment?.createdAt)}</p>
              </div>
              <span className={`px-2 py-0.5 rounded-md text-xs font-medium ${getStatusColor(treatment?.status)}`}>
                {treatment?.status}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <span className="text-slate-500">Dentist:</span>
                <span className="ml-1 text-slate-700">
                  {treatment?.didBy ? `${treatment?.didBy?.firstName} ${treatment?.didBy?.lastName || ""}` : user}
                </span>
              </div>
              <div>
                <span className="text-slate-500">Price:</span>
                <span className="ml-1 font-medium text-slate-800">Rs {treatment?.price}</span>
              </div>
            </div>
            {treatment?.note && (
              <p className="text-[10px] text-slate-500 mt-1.5 truncate">Note: {treatment?.note}</p>
            )}

            {/* Sub Treatments */}
            {treatment?.subTreatment?.length > 0 && (
              <div className="mt-2 pt-2 border-t border-slate-100">
                <p className="text-[10px] font-semibold text-amber-700 mb-1.5">Case Treatments ({treatment.subTreatment.length})</p>
                {treatment.subTreatment.map((sub: any, i: number) => (
                  <div key={i} className="bg-amber-50 rounded-md p-1.5 mb-1.5 last:mb-0">
                    <p className="text-xs font-medium text-amber-800">{sub?.treatment}</p>
                    <p className="text-[10px] text-amber-600">Rs {sub?.price}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </>
    );
  }

  // Desktop Table View
  return (
    <>
      {item?.treatments?.map((treatment: any, index: number) => (
        <Fragment key={index}>
          <tr
            onClick={() => navigate(`/client/treatment-diagnose/add-treatment/diagnose/${nav}`)}
            className="hover:bg-slate-50 cursor-pointer transition-colors"
          >
            <td className="px-3 py-2 text-xs text-slate-600">{formatDate(treatment?.createdAt)}</td>
            <td className="px-3 py-2 text-xs font-medium text-slate-800">
              {treatment?.treatment}
              {treatment?.type ? ` - ${treatment?.type}` : ""}
              {treatment?.subtype ? ` - ${treatment?.subtype}` : ""}
            </td>
            <td className="px-3 py-2 text-xs text-slate-500 max-w-[150px] truncate">
              {insertBreaks(treatment?.note)}
            </td>
            <td className="px-3 py-2 text-xs text-slate-600">
              {treatment?.didBy
                ? `${treatment?.didBy?.firstName} ${treatment?.didBy?.lastName || ""}`
                : user}
            </td>
            <td className="px-3 py-2 text-xs font-medium text-slate-800">Rs {treatment?.price}</td>
            <td className="px-3 py-2">
              <span className={`px-2 py-0.5 rounded-md text-xs font-medium ${getStatusColor(treatment?.status)}`}>
                {treatment?.status}
              </span>
            </td>
          </tr>

          {/* Sub Treatments */}
          {treatment?.subTreatment?.length > 0 && (
            <tr>
              <td colSpan={6} className="p-0">
                <div className="ml-6 my-1.5 bg-amber-50 rounded-lg border border-amber-200 overflow-hidden">
                  <div className="px-3 py-1.5 border-b border-amber-200">
                    <h4 className="text-xs font-semibold text-amber-800">Case Treatment</h4>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-amber-50/50">
                          <th className="px-2 py-1.5 text-left text-xs font-semibold text-amber-700 uppercase">Date</th>
                          <th className="px-2 py-1.5 text-left text-xs font-semibold text-amber-700 uppercase">Treatment</th>
                          <th className="px-2 py-1.5 text-left text-xs font-semibold text-amber-700 uppercase">Tooth No</th>
                          <th className="px-2 py-1.5 text-left text-xs font-semibold text-amber-700 uppercase">Surface</th>
                          <th className="px-2 py-1.5 text-left text-xs font-semibold text-amber-700 uppercase">Price</th>
                          <th className="px-2 py-1.5 text-left text-xs font-semibold text-amber-700 uppercase">Dentist</th>
                          <th className="px-2 py-1.5 text-left text-xs font-semibold text-amber-700 uppercase">Note</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-amber-100">
                        {treatment?.subTreatment?.map((sub: any, i: number) => (
                          <tr key={i} className="hover:bg-amber-50/50">
                            <td className="px-2 py-1.5 text-xs text-amber-800">{formatDate(sub?.createdAt)}</td>
                            <td className="px-2 py-1.5 text-xs font-medium text-amber-900">
                              {sub?.treatment}
                              {sub?.type ? ` - ${sub?.type}` : ""}
                              {sub?.subtype ? ` - ${sub?.subtype}` : ""}
                            </td>
                            <td className="px-2 py-1.5 text-xs text-amber-800">{sub?.toothNo}</td>
                            <td className="px-2 py-1.5 text-xs text-amber-800">{sub?.surface}</td>
                            <td className="px-2 py-1.5 text-xs font-medium text-amber-900">Rs {sub?.price}</td>
                            <td className="px-2 py-1.5 text-xs text-amber-800">
                              {sub?.didBy ? `${sub?.didBy?.firstName} ${sub?.didBy?.lastName || ""}` : user}
                            </td>
                            <td className="px-2 py-1.5 text-xs text-amber-700 max-w-[100px] truncate">{sub?.note}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </td>
            </tr>
          )}
        </Fragment>
      ))}
    </>
  );
}

export default PasTSummary;
